import React, { useState, useRef, useEffect } from 'react'
import dynamic from 'next/dynamic'
import { useIntl } from 'react-intl'

// Import dedicated styles for BTS Agents 25 component
import styles from './bts-agents-25.module.scss'
import { withPublicAssetUrl } from '../../../lib/utils/cdn'
import { SplineViewerProvider } from '@lib/hooks/useSplineViewer'
import { useWebGLSupport } from '@lib/hooks'

// WebGL-based runtime toggle implemented via hook

// Dynamically import the Spline scene component for true bundle-level lazy loading
const SplineScene = dynamic(() => import('./SplineScene'), {
  ssr: false,
})

// Agent hover zones configuration - defines agents and their content
// Positioning is now handled in CSS via data attributes
export const BTS_AGENTS_OVERLAY_CARDS = [
  {
    id: 'expert-review',
    title: 'Expert Review',
    description: 'Get feedback inspired by leading experts in the field',
    icon: 'expert-review',
  },
  {
    id: 'citation-finder',
    title: 'Citation Finder',
    description: 'Check facts and find sources for your claims',
    icon: 'citation-finder',
  },
  {
    id: 'ai-detector',
    title: 'AI Detector',
    description: 'Identify Al-generated content in your writing and fix it',
    icon: 'ai-detector',
  },
  {
    id: 'ai-grader',
    title: 'AI Grader',
    description: 'Predict your grade based on your rubric and instructor',
    icon: 'ai-grader',
  },
  {
    id: 'reader-reactions',
    title: 'Reader Reactions',
    description: "Predict people's reactions and questions before sharing",
    icon: 'reader-reactions',
  },
  {
    id: 'ai-proofreader',
    title: 'Proofreader',
    description: 'Get edits for clarity, flow, structure, and mistakes',
    icon: 'proofreader',
  },
  {
    id: 'plagiarism-checker',
    title: 'Plagiarism Checker',
    description: 'Detect non-original content and add proper citations',
    icon: 'plagiarism-checker',
  },
  {
    id: 'ai-chat',
    title: 'AI Chat',
    description: 'Chat with AI to brainstorm or refine your writing',
    icon: 'ai-chat',
  },
  {
    id: 'paraphraser',
    title: 'Paraphraser',
    description: 'Rewrite text in a specific tone or voice',
    icon: 'paraphraser',
  },
]

/**
 * SVG Icon Component - dynamically loads SVG files from bts-hero/ using CDN helper
 */
const SvgIcon: React.FC<{ iconName: string }> = ({ iconName }) => {
  const intl = useIntl()
  return (
    <div className={styles.iconContainer}>
      <img
        src={withPublicAssetUrl(`bts-hero/overlay-card-${iconName}.svg`)}
        alt={intl.formatMessage(
          {
            id: 'btsAgents25.icon.altText',
            description: 'BTS Agents 25 - SVG Icon - Alt Text',
            defaultMessage: '{iconName} icon',
          },
          { iconName }
        )}
        width="32"
        height="32"
        style={{ display: 'block' }}
      />
    </div>
  )
}

/**
 * Overlay Card Component for BTS Agents
 */
const OverlayCard: React.FC<{
  card: (typeof BTS_AGENTS_OVERLAY_CARDS)[0]
  isVisible: boolean
}> = ({ card, isVisible }) => {
  return (
    <div className={`${styles.overlayCard} ${isVisible ? styles.visible : ''}`} data-agent={card.id}>
      <div className={styles.cardContent}>
        <SvgIcon iconName={card.icon} />
        <div className={styles.textContent}>
          <h3 className={styles.title}>{card.title}</h3>
          <p className={styles.description}>{card.description}</p>
        </div>
      </div>
    </div>
  )
}

/**
 * PNG Fallback Component - Static images for users who cannot run WebGL
 */
const PngFallbackComponent: React.FC<{
  hoveredAgentId: string | null
  setHoveredAgentId: (_id: string | null) => void
}> = ({ hoveredAgentId, setHoveredAgentId }) => {
  const intl = useIntl()
  return (
    <div className={styles.mainContainer}>
      <div className={styles.splineAbsoluteContainer}>
        {/* All agent PNG images */}
        {BTS_AGENTS_OVERLAY_CARDS.map((card, index) => {
          const shouldDim = hoveredAgentId && hoveredAgentId !== card.id

          return (
            <img
              key={`agent-png-${card.id}`}
              src={withPublicAssetUrl(`bts-hero/agent-${card.icon}.png`)}
              alt={intl.formatMessage(
                {
                  id: 'btsAgents25.pngFallbackComponent.image.altText',
                  description: 'BTS Agents 25 - PNG Fallback Component - Image - Alt Text',
                  defaultMessage: '{title} AI writing assistant',
                },
                { title: card.title }
              )}
              className={`${styles.pngAgent} ${shouldDim ? styles.dimmed : ''}`}
              data-agent={card.id}
              style={
                {
                  cursor: 'pointer',
                  '--delay': `${index * 0.2 + 0.1}s`,
                } as React.CSSProperties
              }
              onMouseEnter={() => setHoveredAgentId(card.id)}
              onMouseLeave={() => setHoveredAgentId(null)}
            />
          )
        })}

        {/* Overlay Cards for PNG version */}
        {BTS_AGENTS_OVERLAY_CARDS.map((card) => {
          const isCardVisible = hoveredAgentId === card.id

          return <OverlayCard key={`png-card-${card.id}`} card={card} isVisible={isCardVisible} />
        })}
      </div>
    </div>
  )
}

/**
 * Mobile Agent Display Component - Shows single agent with navigation
 */
export const MobileAgentDisplay: React.FC = () => {
  const intl = useIntl()
  const [currentAgentIndex, setCurrentAgentIndex] = useState(0)
  const currentAgent = BTS_AGENTS_OVERLAY_CARDS[currentAgentIndex]

  const handlePrevious = () => {
    setCurrentAgentIndex(currentAgentIndex === 0 ? BTS_AGENTS_OVERLAY_CARDS.length - 1 : currentAgentIndex - 1)
  }

  const handleNext = () => {
    setCurrentAgentIndex(currentAgentIndex === BTS_AGENTS_OVERLAY_CARDS.length - 1 ? 0 : currentAgentIndex + 1)
  }

  return (
    <div className={styles.mobileContainer}>
      {/* Agent Image - Now positioned above the navigation */}
      <div className={styles.mobileImageContainer}>
        <img
          src={withPublicAssetUrl(`bts-hero/agent-${currentAgent.icon}.png`)}
          alt={intl.formatMessage(
            {
              id: 'btsAgents25.mobileAgentDisplay.image.altText',
              description: 'BTS Agents 25 - Mobile Agent Display - Image - Alt Text',
              defaultMessage: '{title} AI writing assistant',
            },
            { title: currentAgent.title }
          )}
          className={styles.mobileAgentImage}
        />
      </div>

      {/* Navigation Buttons - Positioned at viewport edges */}
      <div className={styles.mobileNavigationContainer}>
        {/* Left Arrow */}
        <button
          className={styles.navButtonLeft}
          onClick={handlePrevious}
          aria-label={intl.formatMessage({
            id: 'btsAgents25.mobileAgentDisplay.previous.ctaText',
            description: 'BTS Agents 25 - Mobile Agent Display - Previous - CTA Text',
            defaultMessage: 'Previous agent',
          })}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M10 12L6 8L10 4"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* Right Arrow */}
        <button
          className={styles.navButtonRight}
          onClick={handleNext}
          aria-label={intl.formatMessage({
            id: 'btsAgents25.mobileAgentDisplay.next.ctaText',
            description: 'BTS Agents 25 - Mobile Agent Display - Next - CTA Text',
            defaultMessage: 'Next agent',
          })}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M6 4L10 8L6 12"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {/* Clean Text Treatment - No background, no icon */}
      <div className={styles.mobileTextContent}>
        <h3 className={styles.mobileTitle}>{currentAgent.title}</h3>
        <p className={styles.mobileDescription}>{currentAgent.description}</p>
      </div>
    </div>
  )
}

/**
 * Custom hook for intersection observer lazy loading
 */
const useIntersectionObserver = (options = {}) => {
  const [isIntersecting, setIsIntersecting] = useState(false)
  const [hasIntersected, setHasIntersected] = useState(false)
  const elementRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const element = elementRef.current
    if (!element) {
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsIntersecting(entry.isIntersecting)
        if (entry.isIntersecting && !hasIntersected) {
          setHasIntersected(true)
        }
      },
      { threshold: 0.1, rootMargin: '50px', ...options }
    )

    observer.observe(element)

    return () => {
      observer.unobserve(element)
    }
  }, [hasIntersected, options])

  return { elementRef, isIntersecting, hasIntersected }
}

/**
 * Main BTS Agents 25 Component - Single Spline Embed with Hover Zones and Overlay Cards
 */
const BtsAgents25Inner: React.FC = () => {
  const intl = useIntl()
  const [hoveredAgentId, setHoveredAgentId] = useState<string | null>(null)
  const [isIframeLoaded, setIsIframeLoaded] = useState(false)
  const { elementRef, hasIntersected, isIntersecting } = useIntersectionObserver()
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const handleIframeLoad = () => {
    setIsIframeLoaded(true)
  }

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current)
      }
    }
  }, [])

  // Show the Spline scene when it's loaded and currently visible
  const shouldShowSpline = hasIntersected && isIframeLoaded && isIntersecting

  const isWebGLSupported = useWebGLSupport()

  const imageAltText = intl.formatMessage({
    id: 'btsAgents25.inner.image.altText',
    description: 'BTS Agents 25 - Inner - Image - Alt Text',
    defaultMessage: 'Collection of AI writing assistants floating in a digital environment',
  })

  // If WebGL is not supported, fall back to PNG version
  if (!isWebGLSupported) {
    return <PngFallbackComponent hoveredAgentId={hoveredAgentId} setHoveredAgentId={setHoveredAgentId} />
  }

  return (
    <div ref={elementRef} className={styles.mainContainer}>
      {/* Mobile Agent Display is now rendered within the hero title for mobile */}

      {/* Tablet Spline Group Display for 513px-768px */}
      <div className={styles.mobileOnly}>
        <img
          src={withPublicAssetUrl('bts-hero/hero-blur.png')}
          alt={imageAltText}
          style={{
            width: '100%',
            height: 'auto',
            maxWidth: '600px',
            margin: '0 auto',
            display: 'block',
          }}
        />
      </div>

      <div className={styles.splineAbsoluteContainer}>
        {/* Fallback image shown when iframe is loading or not yet triggered */}
        {(!hasIntersected || !shouldShowSpline) && (
          <img
            src={withPublicAssetUrl('bts-hero/hero-blur.png')}
            alt={imageAltText}
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              transform: 'scale(1.1)',
              zIndex: 2,
              opacity: shouldShowSpline ? 0 : 1,
              transition: 'opacity 0.8s ease-out',
            }}
          />
        )}

        {/* Spline scenes with provider - lazy loaded when visible */}
        {hasIntersected && (
          <SplineViewerProvider>
            <SplineScene onLoad={handleIframeLoad} shouldLoad={hasIntersected} isVisible={shouldShowSpline} />
          </SplineViewerProvider>
        )}

        {/* Invisible hover zones for agent detection - only active when iframe is loaded */}
        {hasIntersected && (
          <div className={styles.hoverZonesContainer}>
            {BTS_AGENTS_OVERLAY_CARDS.map((card) => (
              <div
                key={`hover-zone-${card.id}`}
                className={styles.hoverZone}
                data-agent={card.id}
                style={{
                  position: 'absolute',
                  zIndex: 25, // Above iframe but below overlays
                  backgroundColor: 'transparent',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={() => {
                  // Clear any existing timeout
                  if (hoverTimeoutRef.current) {
                    clearTimeout(hoverTimeoutRef.current)
                  }
                  // Set hover after 0.1s delay
                  hoverTimeoutRef.current = setTimeout(() => {
                    setHoveredAgentId(card.id)
                  }, 100)
                }}
                onMouseLeave={() => {
                  // Clear timeout if user leaves before delay completes
                  if (hoverTimeoutRef.current) {
                    clearTimeout(hoverTimeoutRef.current)
                    hoverTimeoutRef.current = null
                  }
                  // Clear hover state if this was the hovered agent
                  if (hoveredAgentId === card.id) {
                    setHoveredAgentId(null)
                  }
                }}
              />
            ))}
          </div>
        )}

        {/* Overlay Cards - only active when iframe is loaded */}
        {hasIntersected &&
          BTS_AGENTS_OVERLAY_CARDS.map((card) => {
            const isCardVisible = hoveredAgentId === card.id

            return <OverlayCard key={card.id} card={card} isVisible={isCardVisible} />
          })}
      </div>
    </div>
  )
}

export const BtsAgents25Component: React.FC = () => {
  return <BtsAgents25Inner />
}
